/* Pakhna Sarees – product page redesign */
(() => {
  if (window.PakhnaPdp) return;

  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /* ───────── Gallery ───────── */
  function initGallery(root) {
    const gallery = root.querySelector('[data-pk-gallery]');
    if (!gallery) return;
    const track = gallery.querySelector('[data-pk-slides]');
    const slides = [...gallery.querySelectorAll('[data-pk-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-pk-thumb]')];
    const dots = [...gallery.querySelectorAll('[data-pk-dot]')];
    const counter = gallery.querySelector('[data-pk-counter]');
    let active = 0;

    const setActive = (index) => {
      if (index === active) return;
      const previous = slides[active];
      if (previous) previous.querySelectorAll('video').forEach((video) => video.pause());
      active = index;
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
        if (i === index) {
          thumb.setAttribute('aria-current', 'true');
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      if (counter) counter.textContent = index + 1;
    };

    const goTo = (index, smooth = true) => {
      const slide = slides[index];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      setActive(index);
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => goTo(Number(thumb.dataset.index)));
    });

    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const width = track.clientWidth || 1;
          setActive(Math.round(track.scrollLeft / width));
        });
      },
      { passive: true }
    );

    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') goTo(Math.min(active + 1, slides.length - 1));
      if (event.key === 'ArrowLeft') goTo(Math.max(active - 1, 0));
    });

    // Hover zoom (desktop pointers only; CSS ignores it elsewhere)
    gallery.querySelectorAll('[data-pk-zoom]').forEach((zoom) => {
      zoom.addEventListener('mousemove', (event) => {
        const rect = zoom.getBoundingClientRect();
        zoom.style.setProperty('--pk-zx', `${((event.clientX - rect.left) / rect.width) * 100}%`);
        zoom.style.setProperty('--pk-zy', `${((event.clientY - rect.top) / rect.height) * 100}%`);
        zoom.classList.add('is-zoomed');
      });
      zoom.addEventListener('mouseleave', () => zoom.classList.remove('is-zoomed'));
    });
  }

  /* ───────── Offer code copy ───────── */
  function initCopy(root) {
    root.querySelectorAll('[data-pk-copy]').forEach((button) => {
      const original = button.textContent.trim();
      button.addEventListener('click', async () => {
        const code = button.dataset.pkCopy;
        try {
          await navigator.clipboard.writeText(code);
        } catch (error) {
          const input = document.createElement('input');
          input.value = code;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
        }
        button.textContent = button.dataset.copiedLabel || 'Copied';
        clearTimeout(button._pkTimer);
        button._pkTimer = setTimeout(() => (button.textContent = original), 2000);
      });
    });
  }

  /* ───────── Cart ───────── */
  function buildItems(root) {
    const variantId = Number(root.dataset.variantId);
    const item = { id: variantId, quantity: 1 };
    const items = [item];
    const blouse = root.querySelector('[data-pk-blouse]');
    if (blouse) {
      const choice = blouse.querySelector('input:checked');
      if (choice) {
        const heading = blouse.querySelector('legend')?.textContent.trim() || 'Blouse';
        item.properties = { [heading]: choice.value };
        if (choice.dataset.addonVariant) {
          const title = root.querySelector('.pk-title')?.textContent.trim();
          items.push({
            id: Number(choice.dataset.addonVariant),
            quantity: 1,
            properties: title ? { For: title } : {},
          });
        }
      }
    }
    return items;
  }

  function showError(root, message) {
    const box = root.querySelector('[data-pk-error]');
    if (!box) return;
    if (!message) {
      box.hidden = true;
      box.textContent = '';
      return;
    }
    box.textContent = message;
    box.hidden = false;
  }

  async function addToCart(root, { withSections }) {
    const cartDrawer = document.querySelector('cart-drawer');
    const body = { items: buildItems(root) };
    if (withSections && cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }
    const response = await fetch(`${root.dataset.cartAddUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || window.cartStrings?.error || 'Something went wrong.');
      error.status = response.status;
      throw error;
    }
    return { data, cartDrawer };
  }

  function setLoading(button, loading) {
    if (!button) return;
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-disabled', loading);
  }

  function initForm(root) {
    const form = root.querySelector('[data-pk-form]');
    if (!form) return;
    const addButton = form.querySelector('[data-pk-add]');
    const buyNowButton = form.querySelector('[data-pk-buy-now]');
    let busy = false;

    form.addEventListener(
      'submit',
      async (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (busy || addButton?.disabled) return;
        busy = true;
        showError(root, null);
        setLoading(addButton, true);
        try {
          const { data, cartDrawer } = await addToCart(root, { withSections: true });
          const variantId = Number(root.dataset.variantId);
          // PUB_SUB_EVENTS is a top-level const in Dawn's constants.js, so it isn't on window.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'pakhna-pdp',
              productVariantId: variantId,
              cartData: data,
            });
          }
          if (cartDrawer && data.sections && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents({ id: variantId, sections: data.sections });
          } else {
            window.location.href = root.dataset.cartUrl;
          }
        } catch (error) {
          showError(root, error.message);
        } finally {
          busy = false;
          setLoading(addButton, false);
        }
      },
      true
    );

    if (buyNowButton) {
      buyNowButton.addEventListener('click', async () => {
        if (busy) return;
        busy = true;
        showError(root, null);
        setLoading(buyNowButton, true);
        const goToCheckout = () => {
          const checkoutButton = root.querySelector('[data-pk-checkout-form] button[name="checkout"]');
          if (checkoutButton) checkoutButton.click();
          else window.location.href = `${root.dataset.rootUrl.replace(/\/$/, '')}/checkout`;
        };
        try {
          const cart = await fetch(`${root.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } }).then(
            (response) => response.json()
          );
          const variantId = Number(root.dataset.variantId);
          const alreadyInCart = cart.items?.some((item) => item.variant_id === variantId);
          if (!alreadyInCart) await addToCart(root, { withSections: false });
          goToCheckout();
        } catch (error) {
          showError(root, error.message);
          setLoading(buyNowButton, false);
        } finally {
          busy = false;
        }
      });
    }
  }

  /* ───────── Delivery estimate ───────── */
  function initDelivery(root) {
    const box = root.querySelector('[data-pk-delivery]');
    if (!box) return;
    const eta = box.querySelector('[data-pk-eta]');
    const timeEl = box.querySelector('[data-pk-eta-time]');
    const dateEl = box.querySelector('[data-pk-eta-date]');
    if (!eta) return;

    const cutoff = Number(box.dataset.cutoff) || 14;
    const days = Number(box.dataset.days) || 5;
    const skipSunday = box.dataset.skipSunday === 'true';
    const isWorkingDay = (date) => !(skipSunday && date.getDay() === 0);

    const render = () => {
      const now = new Date();
      const dispatch = new Date(now);
      dispatch.setHours(cutoff, 0, 0, 0);
      if (now >= dispatch || !isWorkingDay(dispatch)) {
        do {
          dispatch.setDate(dispatch.getDate() + 1);
        } while (!isWorkingDay(dispatch));
      }

      const delivery = new Date(dispatch);
      let added = 0;
      while (added < days) {
        delivery.setDate(delivery.getDate() + 1);
        if (isWorkingDay(delivery)) added += 1;
      }

      const minutesLeft = Math.max(1, Math.ceil((dispatch - now) / 60000));
      const hours = Math.floor(minutesLeft / 60);
      const minutes = minutesLeft % 60;
      if (timeEl) timeEl.textContent = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
      if (dateEl) dateEl.textContent = `${DAYS[delivery.getDay()]}, ${delivery.getDate()} ${MONTHS[delivery.getMonth()]}`;
      eta.hidden = false;
    };

    render();
    clearInterval(box._pkTimer);
    box._pkTimer = setInterval(render, 30000);
  }

  /* ───────── Sticky mobile bar ───────── */
  function initSticky(root) {
    const bar = root.querySelector('[data-pk-sticky]');
    const addButton = root.querySelector('[data-pk-add]');
    if (!bar || !addButton) return;
    const stickyButton = bar.querySelector('[data-pk-sticky-add]');

    // A scroll check (not IntersectionObserver) so jumps past the button, e.g. anchor links, still show the bar.
    let ticking = false;
    const update = () => {
      ticking = false;
      const passed = addButton.getBoundingClientRect().bottom < 0;
      bar.classList.toggle('is-visible', passed);
      bar.setAttribute('aria-hidden', String(!passed));
      stickyButton.tabIndex = passed ? 0 : -1;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();

    stickyButton.addEventListener('click', () => addButton.click());
  }

  /* ───────── Recommendations ───────── */
  async function loadRecommendations(container) {
    if (container.dataset.pkLoaded) return;
    container.dataset.pkLoaded = 'true';
    const baseUrl = container.dataset.url;
    const fetchInner = async (intent) => {
      const response = await fetch(`${baseUrl}&intent=${intent}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.querySelector('[data-pk-recs-inner]');
      return inner && inner.querySelector('.pk-card') ? inner.innerHTML : '';
    };
    try {
      let html = await fetchInner(container.dataset.intent || 'related');
      if (!html && container.dataset.intent === 'complementary') html = await fetchInner('related');
      if (html) container.querySelector('[data-pk-recs-inner]').innerHTML = html;
    } catch (error) {
      console.error(error);
    }
  }

  function initRecommendations(scope) {
    scope.querySelectorAll('[data-pk-recs]').forEach((container) => {
      if (!('IntersectionObserver' in window)) return loadRecommendations(container);
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          loadRecommendations(container);
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(container);
    });
  }

  /* ───────── Boot ───────── */
  function init(scope = document) {
    scope.querySelectorAll('[data-pk-product]').forEach((root) => {
      if (root.dataset.pkReady) return;
      root.dataset.pkReady = 'true';
      initGallery(root);
      initCopy(root);
      initForm(root);
      initDelivery(root);
      initSticky(root);
    });
    initRecommendations(scope);
  }

  window.PakhnaPdp = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
